import html2canvas from 'html2canvas';
import { alertService } from '../services/alertService';
import { exportedOnText } from './stamp';

// ─────────────────────────────────────────────
// DOM-to-image export (PNG / JPG)
//
// Renders an existing DOM element (e.g. a report table) to a raster image and
// triggers a download. Used for the Table export PNG/JPG buttons — mirrors
// AppChart's own PNG/JPG download (same footer-stamp convention, same file
// naming), but captures arbitrary DOM instead of a <canvas> plot.
// ─────────────────────────────────────────────

export interface ElementImageExportOptions {
  /** The element to capture (e.g. the table's scroll wrapper). */
  element: HTMLElement;
  /** Output file name (without extension). */
  fileName?: string;
  /** Raster format. Defaults to 'png'. */
  format?: 'png' | 'jpg';
  /** Optional heading drawn in the footer strip, right-aligned. */
  title?: string;
}

/**
 * Capture a DOM element to PNG/JPG and download it. Adds a footer strip below
 * the captured content with the "Exported on …" stamp (left) and an optional
 * title (right), same layout AppChart uses for its own PNG/JPG export, so
 * every exported image in the app reads consistently.
 */
export async function exportElementToImage({
  element,
  fileName = 'export',
  format = 'png',
  title,
}: ElementImageExportOptions): Promise<void> {
  if (element.offsetWidth === 0 || element.offsetHeight === 0) {
    alertService.warn('Nothing to export.');
    return;
  }

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
  });

  // Footer strip added BELOW the captured content, scaled to match the
  // capture's device-pixel density (html2canvas's `scale: 2` above) so the
  // caption isn't hairline-thin on a HiDPI export.
  const ratio = canvas.width / element.offsetWidth || 2;
  const fontPx = Math.max(11, Math.round(12 * ratio));
  const footerH = Math.round(fontPx * 2.2);

  const out = document.createElement('canvas');
  out.width = canvas.width;
  out.height = canvas.height + footerH;
  const ctx = out.getContext('2d');
  if (ctx) {
    // Flatten onto white so a JPG export never shows black where the source
    // had transparency.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, 0);
    ctx.fillStyle = '#6b7280';
    ctx.font = `${fontPx}px Arial, Helvetica, sans-serif`;
    ctx.textBaseline = 'middle';
    const midY = canvas.height + footerH / 2;
    ctx.textAlign = 'left';
    ctx.fillText(exportedOnText(), Math.round(fontPx * 0.8), midY);
    if (title) {
      ctx.textAlign = 'right';
      ctx.fillText(title, out.width - Math.round(fontPx * 0.8), midY);
    }
  }

  const mime = format === 'jpg' ? 'image/jpeg' : 'image/png';
  const url = out.toDataURL(mime, 1);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.${format}`;
  link.click();
}
